import {
    FunctionComponent,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react"
import {
    WELL_DETAILS_ABS_ROUTE,
    WELL_NOTES_ABS_ROUTE,
    WELL_NOTES_UPDATE_CHILD_ROUTE,
} from "../../../.."
import Button from "../../../shared/button"
import { WellNotesForIwpFieldsFragment } from "../../../../generated/graphql"
import { Link, useNavigate } from "react-router-dom"
import styles from "./list.module.scss"
import Fuse from "fuse.js"
import dayjs from "dayjs"
import { useVirtualizer, Virtualizer } from "@tanstack/react-virtual"
import WellNotesListHeader from "./header"
import { sortArrayByKey } from "../../../../util/arrays/array"
import { useBreakPointContext } from "../../../../context/breakPointContext"
import { WellDetailsQryParams } from "../../wellDetailsChart"

// Types
export interface WellNotesFilterI {
    createdBy: string[]
    dateRange: {
        startDate: string
        endDate: string
    }
}

export interface WellNotesSortConfigI {
    field: "occurredAt"
    direction: "asc" | "desc"
}

interface WellNotesListPropsI {
    wellNotes: WellNotesForIwpFieldsFragment[]
}

// Component
const WellNotesList: FunctionComponent<WellNotesListPropsI> = ({
    wellNotes,
}) => {
    // state
    const [searchInputTerm, setSearchInputTerm] = useState("")

    const [sortConfig, setSortConfig] = useState<WellNotesSortConfigI>({
        field: "occurredAt",
        direction: "desc",
    })

    const [filters, setFilters] = useState<WellNotesFilterI>({
        createdBy: [],
        dateRange: {
            startDate: "",
            endDate: "",
        },
    })

    // Extract all unique users for filter dropdowns
    const uniqueUsers = useMemo(() => {
        const userMap = new Map<string, string>()

        wellNotes.forEach((note) => {
            // Add created by user
            if (note.createdBy.identity?.fullName) {
                userMap.set(note.createdBy.id, note.createdBy.identity.fullName)
            }
        })

        return Array.from(userMap.entries()).map(([id, name]) => ({ id, name }))
    }, [wellNotes])

    const parentRef = useRef<HTMLDivElement>(null)

    const fuse = useMemo(() => {
        return new Fuse(wellNotes, {
            keys: [
                "note",
                "createdBy.identity.fullName",
                "updatedBy.identity.fullName",
                {
                    name: "occurredAt",
                    getFn: (note) =>
                        dayjs
                            .unix(note.occurredAt.unixSeconds)
                            .format("MMM D, YYYY h:mm A"),
                },
            ],
            threshold: 0,
            ignoreLocation: true,
        })
    }, [wellNotes])

    // Apply search, filter, and sort to get processed notes
    const processedNotes = useMemo(() => {
        // apply search if there's a search term
        let notes: WellNotesForIwpFieldsFragment[] = wellNotes

        if (searchInputTerm.trim()) {
            const searchResults = fuse.search(searchInputTerm)
            notes = searchResults.map((res) => res.item)
        }

        //  apply filters
        if (filters.createdBy.length > 0) {
            notes = notes.filter((note) =>
                filters.createdBy.includes(note.createdBy.id)
            )
        }

        // Apply date range filter
        if (filters.dateRange?.startDate) {
            // get start of day for selected filter in local time, then convert to UTC to compare against the note's occurred at timestamp stored in utc.
            const startTimestamp = dayjs(filters.dateRange.startDate)
                .startOf("day")
                .utc()
                .unix()

            notes = notes.filter(
                (note) => note.occurredAt.unixSeconds >= startTimestamp
            )
        }

        if (filters.dateRange?.endDate) {
            // get end of day for selected filter in local time, then convert to UTC to compare against the note's occurred at timestamp stored in utc.
            const endTimestamp = dayjs(filters.dateRange.endDate)
                .endOf("day")
                .utc()
                .unix()

            notes = notes.filter(
                (note) => note.occurredAt.unixSeconds <= endTimestamp
            )
        }

        // apply sort
        const sortedNotes = sortArrayByKey(
            notes,
            (note) => note.occurredAt.unixSeconds,
            false,
            sortConfig.direction
        )

        return sortedNotes
    }, [wellNotes, searchInputTerm, filters, sortConfig, fuse])

    // Initialize the virtualizer
    const rowVirtualizer = useVirtualizer({
        count: processedNotes.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 250, // Estimate the height of each note card
        overscan: 5, // Render additional items above and below for smoother scrolling
        gap: 12, // Adds space between items
    })

    // Reset scroll position when filters change drastically
    const scrollToTop = useCallback(() => {
        if (parentRef.current) {
            rowVirtualizer.scrollToIndex(0)
        }
    }, [rowVirtualizer])

    return (
        <>
            <div className={styles.componentContianer}>
                <WellNotesListHeader
                    wellId={wellNotes[0].well.id}
                    searchTerm={searchInputTerm}
                    handleSearchTermChange={(value) => {
                        setSearchInputTerm(value)
                        scrollToTop()
                    }}
                    sortConfig={sortConfig}
                    handleSortConfigChange={(config) => {
                        setSortConfig(config)
                        scrollToTop()
                    }}
                    filters={filters}
                    onFilterChange={(newFilters) => {
                        setFilters(newFilters)
                        scrollToTop()
                    }}
                    uniqueUsers={uniqueUsers}
                    resetAll={() => {
                        setFilters({
                            createdBy: [],
                            dateRange: {
                                startDate: "",
                                endDate: "",
                            },
                        })
                        setSortConfig({
                            field: "occurredAt",
                            direction: "desc",
                        })
                        setSearchInputTerm("")
                        scrollToTop()
                    }}
                />
            </div>
            <div className={styles.componentContianer}>
                <WellNotesListSummary
                    filters={filters}
                    processedNotesCount={processedNotes.length}
                    searchInputTerm={searchInputTerm}
                    totalNotesCount={wellNotes.length}
                />
            </div>
            <div className={styles.componentContianer}>
                <List
                    rowVirtualizer={rowVirtualizer}
                    processedNotes={processedNotes}
                    searchInputTerm={searchInputTerm}
                    parentRef={parentRef}
                />
            </div>
        </>
    )
}

export default WellNotesList

interface WellNotesListSummaryProps {
    searchInputTerm: string
    filters: {
        createdBy: string[]

        dateRange?: {
            startDate: string
            endDate: string
        }
    }
    processedNotesCount: number
    totalNotesCount: number
}

const WellNotesListSummary: FunctionComponent<WellNotesListSummaryProps> = ({
    searchInputTerm,
    filters,
    processedNotesCount,
    totalNotesCount,
}) => {
    const isEmpty = processedNotesCount === 0

    const hasFilters =
        searchInputTerm ||
        filters.createdBy.length > 0 ||
        (filters.dateRange &&
            (filters.dateRange.startDate || filters.dateRange.endDate))

    return (
        <div className={styles.summaryContainer}>
            {hasFilters && !isEmpty && (
                <div className={styles.resultCount}>
                    Showing {processedNotesCount} of {totalNotesCount} notes
                </div>
            )}
            {isEmpty && (
                <div className={styles.noResults}>
                    No notes match your search. Try adjusting your filters.
                </div>
            )}
        </div>
    )
}

interface ListPropsI {
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>
    processedNotes: WellNotesForIwpFieldsFragment[]
    searchInputTerm: string
    parentRef: React.RefObject<HTMLDivElement>
}

// List is the component that makes use of tanstack virtualizer.
// I choose inline styling b/c of the most recent examples provided by tanstack at the time of developing this component.
// https://tanstack.com/virtual/latest
const List: FunctionComponent<ListPropsI> = ({
    rowVirtualizer,
    processedNotes,
    searchInputTerm,
    parentRef,
}) => {
    const breakPoint = useBreakPointContext()

    /** constants */

    // determine input orientation based on small breakpoint
    // src/styles/_breakpoint.scss  && src/context/breakPointContext/index.tsx
    const isSmallBreakPoint = breakPoint === "sm"
    const maxHeight = isSmallBreakPoint ? "unset" : "150px"

    if (!processedNotes.length) {
        return (
            <div
                ref={parentRef}
                style={{
                    height: 500,
                    flex: 1,
                    overflowY: "auto",
                    contain: "strict",
                }}
                className={styles.listContainer}
            />
        )
    }

    return (
        // scrolable element
        <div
            ref={parentRef}
            style={{
                height: 500,
                flex: 1,
                overflowY: "auto",
                contain: "strict",
            }}
            className={styles.listContainer}
        >
            {/* The large inner element to hold all of the items */}
            <div
                style={{
                    height: rowVirtualizer.getTotalSize(),
                    width: "100%",
                    position: "relative",
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const wellNote = processedNotes[virtualRow.index]
                    const unixMsEod = dayjs().endOf("day").valueOf()
                    const isFutureNote =
                        wellNote.occurredAt.unixMilliseconds > unixMsEod

                    return (
                        <div
                            key={virtualRow.key.toString()}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            className={styles.noteCard}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                overflowY: "auto",
                                maxHeight: maxHeight,
                                height: `${processedNotes[virtualRow.index]}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                        >
                            <div className={styles.noteContent}>
                                {highlightSearchTerms(
                                    wellNote.note,
                                    searchInputTerm
                                )}
                            </div>
                            <div className={styles.noteMeta}>
                                <div>
                                    <OccurredAtDate
                                        occurredAtUnixMs={
                                            wellNote.occurredAt.unixMilliseconds
                                        }
                                        searchInputTerm={searchInputTerm}
                                        wellId={wellNote.well.id}
                                        isLink={!isFutureNote}
                                    />
                                    <div>
                                        Created by:{" "}
                                        {highlightSearchTerms(
                                            wellNote.createdBy.identity
                                                ?.fullName || "",
                                            searchInputTerm
                                        )}
                                    </div>
                                    <div>
                                        Updated by:{" "}
                                        {highlightSearchTerms(
                                            wellNote.updatedBy.identity
                                                ?.fullName || "",
                                            searchInputTerm
                                        )}
                                    </div>
                                </div>
                                {isSmallBreakPoint && (
                                    <div>
                                        <UpdateNoteBtn
                                            wellId={wellNote.well.id}
                                            noteId={wellNote.id}
                                        />
                                    </div>
                                )}
                            </div>
                            {!isSmallBreakPoint && (
                                <div className={styles.updateContainer}>
                                    <UpdateNoteBtn
                                        wellId={wellNote.well.id}
                                        noteId={wellNote.id}
                                    />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// Components

// OccurredAtDate displays the date of the note and highlights the search term if present.
// It also provides a link to the well details page if isLink is true. If the note is a future date we do not want to display a link
const OccurredAtDate: FunctionComponent<{
    occurredAtUnixMs: number
    wellId: string
    searchInputTerm: string
    isLink: boolean
}> = ({ occurredAtUnixMs, wellId, searchInputTerm, isLink }) => {
    const formattedDate = highlightSearchTerms(
        dayjs(occurredAtUnixMs).format("MMM D, YYYY h:mm A"),
        searchInputTerm
    )

    const content = (
        <div className={styles.noteDate}>Occurred at: {formattedDate}</div>
    )

    if (isLink) {
        const iwpOverviewUrl = buildWellDetailsUrl(wellId, occurredAtUnixMs)
        return (
            <Link to={iwpOverviewUrl} className={styles.link}>
                {content}
            </Link>
        )
    }

    return content
}

// UpdateNoteBtn is a button that navigates to the update note modal for the given well and note ID.
const UpdateNoteBtn: FunctionComponent<{
    wellId: string
    noteId: string
}> = ({ wellId, noteId }) => {
    const navigate = useNavigate()

    return (
        <Button
            status="secondary"
            customButtonClassName="button-table-row-outlined"
            condition="default"
            disabled={false}
            handleButtonClick={() =>
                navigate(
                    `${WELL_NOTES_ABS_ROUTE(
                        wellId
                    )}/${WELL_NOTES_UPDATE_CHILD_ROUTE(noteId)}`
                )
            }
        >
            Update
        </Button>
    )
}

// helpers

export const highlightSearchTerms = (
    text: string,
    searchTerm: string
): ReactNode => {
    const trimmedSearch = searchTerm.trim()
    if (!trimmedSearch) {
        return text
    }

    try {
        // Escape special regex characters in the search term
        const escaped = trimmedSearch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

        // Build a case-insensitive regex to find the exact substring
        const regex = new RegExp(`(${escaped})`, "gi")

        // Split the text by the search term, keeping the matches using a capture group
        const parts = text.split(regex)

        // Wrap matching parts in <mark>, leave others unchanged
        return parts.map((part, index) =>
            part.toLowerCase() === trimmedSearch.toLowerCase() ? (
                <mark key={index}>{part}</mark>
            ) : (
                part
            )
        )
    } catch (e) {
        console.error("Error highlighting search terms:", e)
        return text
    }
}

// Generate the well details overview URL
// This function builds the URL for the well details page with the appropriate query parameters.
function buildWellDetailsUrl(wellId: string, occurredAt: number): string {
    const noteTime = dayjs(occurredAt)

    // motionDate should be the start of the day we want to view - in browsers local time zone
    const motionDate = noteTime.startOf("day").valueOf()
    // toValue should be the start of the day for the last day on the run time chart we want to view - in browsers local time zone
    const toValue = noteTime.startOf("day").valueOf()
    // fromValue should be the start of the day for the first day on the run time chart we want to view - in browsers local time zone
    const fromValue = noteTime.startOf("day").subtract(30, "day").valueOf()

    const url = `${WELL_DETAILS_ABS_ROUTE(wellId)}?${
        WellDetailsQryParams.motionDate
    }=${motionDate}&${WellDetailsQryParams.dateInterval}=day&${
        WellDetailsQryParams.from
    }=${fromValue}&${WellDetailsQryParams.to}=${toValue}`

    return url
}
